import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CalendarBlank, Crown, Envelope, Eye, Gear, User } from '@phosphor-icons/react';
import {
  maintenanceMessageSchema,
  maintenanceWindowSchema,
  type MaintenanceCommand,
  type MaintenanceState,
} from '@mybusinessmate/schema';
import { LastChanged, SettingCard, SettingRow } from '../../../../../../components/settings/setting-card';
import { useStaffReauth } from '../../../../../../components/settings/use-staff-reauth';
import { Banner } from '../../../../../../components/ui/banner';
import { Button } from '../../../../../../components/ui/button';
import { Dialog } from '../../../../../../components/ui/dialog';
import { Input } from '../../../../../../components/ui/input';
import { Select } from '../../../../../../components/ui/select';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { TextArea } from '../../../../../../components/ui/text-area';
import { Toggle } from '../../../../../../components/ui/toggle';
import { useToast } from '../../../../../../components/ui/toast';
import {
  AUTO_OFF_CHOICES,
  autoOffLabel,
  effectiveMaintenance,
  localInputToIso,
  maintenanceEndsAt,
} from '../../../../../../lib/maintenance';
import { Skeleton } from '../../../../../../components/ui/skeleton';
import { useMaintenance, useMaintenanceCommand } from '../../../../../../lib/workspace-api';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/maintenance')({
  component: MaintenancePage,
});

const STAMP = new Intl.DateTimeFormat('en-IN', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const CLOCK = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
const stamp = (iso: string) => STAMP.format(new Date(iso)).replace(',', '');
const clock = (iso: string) => CLOCK.format(new Date(iso)).toUpperCase();

/** What a client sees while maintenance is on: the same message, with the end time we add for you. */
function ClientPreview({ message, endsAt }: { message: string; endsAt?: string }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-1) rounded-(--mbm-radius-md) bg-surface-muted p-(--mbm-space-4)">
      <span className="text-overline text-text-section">What clients see</span>
      <span className="text-heading-h3 text-text-primary">Back soon</span>
      <span className="text-body-small text-text-secondary">
        {message}
        {endsAt ? ` We’ll be back by ${clock(endsAt)}.` : ''}
      </span>
    </div>
  );
}

function MaintenancePage() {
  const state = useMaintenance();
  const command = useMaintenanceCommand();
  if (!state.data) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  return <MaintenanceForm m={state.data} run={(c, done) => command.mutate(c, { onSuccess: done })} />;
}

function MaintenanceForm({
  m,
  run,
}: {
  m: MaintenanceState;
  /** Sends one command to the server, which works out the times; `done` runs once it was applied. */
  run: (command: MaintenanceCommand, done?: () => void) => void;
}) {
  const toast = useToast();
  const { guard, dialog: reauthDialog } = useStaffReauth();

  // The stored mode can be stale (a window starts and ends on its own), so look again every 30 seconds.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const mode = effectiveMaintenance(m, now);
  const endsAt = maintenanceEndsAt(m);

  const [message, setMessage] = useState(m.message);
  const [messageError, setMessageError] = useState<string>();
  const [turnOnOpen, setTurnOnOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [autoOff, setAutoOff] = useState<string>(AUTO_OFF_CHOICES[0].id);
  const [schedule, setSchedule] = useState({ start: '', end: '' });
  const [scheduleError, setScheduleError] = useState<string>();
  const [extendHours, setExtendHours] = useState('1');

  // The server enforces it: while it is on, clients and other staff get the 'back soon' page and admins always get in.
  const turnOnNow = () => {
    const choice = AUTO_OFF_CHOICES.find((c) => c.id === autoOff) ?? AUTO_OFF_CHOICES[0];
    run({ action: 'turn_on', autoOffHours: choice.hours }, () => {
      setTurnOnOpen(false);
      toast({ title: 'Maintenance mode is on' });
    });
  };

  const turnOff = () => run({ action: 'turn_off' }, () => toast({ title: 'Maintenance mode is off' }));

  const saveSchedule = () => {
    const startsAt = localInputToIso(schedule.start);
    const endsAtIso = localInputToIso(schedule.end);
    const parsed = maintenanceWindowSchema.safeParse({ startsAt, endsAt: endsAtIso });
    if (!startsAt || !endsAtIso) return setScheduleError('Pick a start and an end');
    if (!parsed.success) return setScheduleError(parsed.error.issues[0]?.message);
    if (new Date(endsAtIso) <= new Date()) return setScheduleError('The window has to end in the future');
    run({ action: 'schedule', startsAt: parsed.data.startsAt, endsAt: parsed.data.endsAt }, () => {
      setScheduleOpen(false);
      setScheduleError(undefined);
      toast({
        title: 'Maintenance window scheduled',
        description: 'Clients see a heads-up banner 24 hours before it starts.',
      });
    });
  };

  const commitMessage = () => {
    const parsed = maintenanceMessageSchema.safeParse(message);
    if (!parsed.success) return setMessageError(parsed.error.issues[0]?.message);
    setMessageError(undefined);
    if (parsed.data !== m.message) run({ action: 'settings', message: parsed.data });
  };

  return (
    <>
      {mode === 'scheduled' && m.window ? (
        <Banner
          tone="warning"
          title={`Maintenance scheduled for ${stamp(m.window.startsAt)} – ${clock(m.window.endsAt)}`}
          action={
            <Button variant="ghost" size="small" onClick={turnOff}>
              Cancel window
            </Button>
          }
        >
          Clients already see a heads-up banner.
        </Banner>
      ) : null}
      {mode === 'on' ? (
        <div
          role="status"
          className="flex items-center justify-between gap-(--mbm-space-4) rounded-(--mbm-radius-md) bg-action-primary p-(--mbm-space-4) text-text-on-dark"
        >
          <div className="flex flex-col">
            <span className="text-label-large">Maintenance mode is on</span>
            <span className="text-body-small">
              {m.since ? `Since ${clock(m.since)} · ` : ''}clients and other staff see the ‘back soon’ page
              {endsAt ? ` · ends ${clock(endsAt)}` : ''}
            </span>
          </div>
          <Button variant="ghost" size="small" className="text-text-on-dark" onClick={turnOff}>
            Turn off now
          </Button>
        </div>
      ) : null}

      <SettingCard
        title="Maintenance mode"
        subtitle="Shows clients a calm ‘back soon’ page while you fix or update things. Staff tools keep working for admins."
      >
        <SettingRow
          icon={Gear}
          title={mode === 'on' ? 'Maintenance mode' : 'Turn on maintenance mode now'}
          subtitle={
            mode === 'on'
              ? `On${m.since ? ` since ${clock(m.since)}` : ''}${endsAt ? ` · turns off on its own at ${clock(endsAt)}` : ''}`
              : 'Off · everyone can use everything'
          }
          trailing={
            <Toggle
              aria-label="Maintenance mode"
              checked={mode === 'on'}
              onCheckedChange={(on) => (on ? guard('turn on maintenance mode', () => setTurnOnOpen(true)) : turnOff())}
            />
          }
        />
        {mode === 'on' ? (
          endsAt ? (
            <SettingRow
              icon={CalendarBlank}
              title="Ends at"
              subtitle={`${stamp(endsAt)} IST`}
              trailing={
                <Button variant="secondary" size="row" onClick={() => setExtendOpen(true)}>
                  Extend
                </Button>
              }
            />
          ) : null
        ) : mode === 'scheduled' && m.window ? (
          <SettingRow
            icon={CalendarBlank}
            title="Window scheduled"
            subtitle={`${stamp(m.window.startsAt)} – ${clock(m.window.endsAt)} IST · set by ${m.lastChange?.by ?? 'an admin'}`}
            trailing={
              <Button variant="secondary" size="row" onClick={() => setScheduleOpen(true)}>
                Edit
              </Button>
            }
          />
        ) : (
          <SettingRow
            icon={CalendarBlank}
            title="Schedule a window instead"
            subtitle="Clients see a heads-up banner 24 hours before it starts"
            trailing={
              <Button variant="secondary" size="row" onClick={() => setScheduleOpen(true)}>
                Schedule
              </Button>
            }
          />
        )}
      </SettingCard>

      <SettingCard title="What clients see" subtitle="Keep it short. We add the end time for you.">
        <TextArea
          variant="filled"
          label="Message"
          rows={3}
          maxLength={280}
          value={message}
          error={messageError}
          onChange={(e) => setMessage(e.target.value)}
          onBlur={commitMessage}
        />
        <SettingRow
          icon={Eye}
          title="Preview the maintenance page"
          trailing={
            <Button variant="secondary" size="row" onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
          }
        />
      </SettingCard>

      <SettingCard title="During maintenance">
        <SettingRow
          icon={Crown}
          title="Admins can still sign in"
          subtitle="Always, so you can check things before turning it off"
          trailing={<StatusTag label="Always" tone="green" dot />}
        />
        <SettingRow
          icon={User}
          title="Let other staff keep working"
          subtitle={
            m.staffKeepWorking
              ? 'On · leads, specialists and account managers can keep working'
              : 'Off · leads, specialists and account managers see the same page as clients'
          }
          trailing={
            <Toggle
              aria-label="Let other staff keep working"
              checked={m.staffKeepWorking}
              onCheckedChange={(on) => run({ action: 'settings', staffKeepWorking: on })}
            />
          }
        />
        <SettingRow
          icon={Envelope}
          title="Email clients with something waiting on them"
          subtitle="Only clients with a review or meeting during the window"
          trailing={
            <Toggle
              aria-label="Email clients with something waiting on them"
              checked={m.emailWaitingClients}
              onCheckedChange={(on) => run({ action: 'settings', emailWaitingClients: on })}
            />
          }
        />
      </SettingCard>

      <LastChanged
        prefix="Last changed"
        by={m.lastChange?.by}
        date={m.lastChange ? `${stamp(m.lastChange.at)}` : undefined}
      />

      {reauthDialog}

      <Dialog
        open={turnOnOpen}
        onClose={() => setTurnOnOpen(false)}
        icon={Gear}
        title="Turn on maintenance mode now?"
        description="Clients and staff will see the ‘back soon’ page right away. Anyone uploading a file may need to try again."
      >
        <ClientPreview
          message={m.message}
          endsAt={(() => {
            const choice = AUTO_OFF_CHOICES.find((c) => c.id === autoOff);
            return choice?.hours ? new Date(Date.now() + choice.hours * 3_600_000).toISOString() : undefined;
          })()}
        />
        <Select
          variant="filled"
          label="Turn off automatically"
          value={autoOff}
          onChange={(e) => setAutoOff(e.target.value)}
        >
          {AUTO_OFF_CHOICES.map((c) => (
            <option key={c.id} value={c.id}>
              {autoOffLabel(c)}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-2 gap-(--mbm-space-3)">
          <Button variant="secondary" size="compact" onClick={() => setTurnOnOpen(false)}>
            Cancel
          </Button>
          <Button size="compact" onClick={turnOnNow}>
            Turn on now
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={scheduleOpen}
        onClose={() => {
          setScheduleOpen(false);
          setScheduleError(undefined);
        }}
        title="Schedule a maintenance window"
        description="Clients see a heads-up banner 24 hours before it starts, and ‘back soon’ during the window."
      >
        <div className="flex flex-col gap-(--mbm-space-4)">
          <Input
            variant="filled"
            type="datetime-local"
            label="Starts"
            value={schedule.start}
            onChange={(e) => setSchedule((s) => ({ ...s, start: e.target.value }))}
          />
          <Input
            variant="filled"
            type="datetime-local"
            label="Ends"
            value={schedule.end}
            error={scheduleError}
            onChange={(e) => {
              setSchedule((s) => ({ ...s, end: e.target.value }));
              setScheduleError(undefined);
            }}
          />
        </div>
        <div className="grid grid-cols-2 gap-(--mbm-space-3)">
          <Button variant="secondary" size="compact" onClick={() => setScheduleOpen(false)}>
            Cancel
          </Button>
          <Button size="compact" onClick={saveSchedule}>
            Schedule
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={extendOpen}
        onClose={() => setExtendOpen(false)}
        title="Extend maintenance"
        description="Push the automatic end later."
      >
        <Select variant="filled" label="Extend by" value={extendHours} onChange={(e) => setExtendHours(e.target.value)}>
          <option value="1">1 hour</option>
          <option value="2">2 hours</option>
          <option value="4">4 hours</option>
        </Select>
        <div className="grid grid-cols-2 gap-(--mbm-space-3)">
          <Button variant="secondary" size="compact" onClick={() => setExtendOpen(false)}>
            Cancel
          </Button>
          <Button
            size="compact"
            onClick={() => run({ action: 'extend', hours: Number(extendHours) }, () => setExtendOpen(false))}
          >
            Extend
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Maintenance page"
        description="This is what clients see."
      >
        <ClientPreview message={message} endsAt={endsAt} />
        <Button size="compact" onClick={() => setPreviewOpen(false)}>
          Close
        </Button>
      </Dialog>
    </>
  );
}
